import { type FC, useEffect, useMemo } from 'react';

import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { type Editor, useEditorState } from '@tiptap/react';
import cn from 'classnames';

import { POPOVER_PANEL_CLASSES, tr } from '../../constants';
import { type LinkPromptAction, linkPromptKey } from './LinkPrompt';

interface LinkPromptMenuProps {
  editor: Editor;
  translations?: Record<string, string>;
}

export const LinkPromptMenu: FC<LinkPromptMenuProps> = ({ editor, translations }) => {
  const prompt = useEditorState({
    editor,
    selector: ({ editor: e }) => linkPromptKey.getState(e.state) ?? null,
  });

  const floating = useFloating({
    open: Boolean(prompt),
    onOpenChange: (next) => {
      if (!next) editor.commands.dismissLinkPrompt();
    },
    placement: 'bottom-start',
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const interactions = useInteractions([useDismiss(floating.context, { escapeKey: false })]);

  const from = prompt?.from;
  const to = prompt?.to;
  useEffect(() => {
    if (from === undefined || to === undefined) return;
    floating.refs.setPositionReference({
      // Measured lazily on every autoUpdate pass: the composer body scrolls
      // without dispatching a transaction, so a snapshot rect would leave the
      // menu pinned mid-air. Spanning the whole link (it can wrap across
      // lines) lands the menu below its last line.
      getBoundingClientRect: () => {
        // autoUpdate can still query after the editor is torn down or the doc
        // shrank; a zero rect is harmless because the prompt is gone too.
        if (editor.isDestroyed || to > editor.state.doc.content.size) {
          return { x: 0, y: 0, width: 0, height: 0, top: 0, bottom: 0, left: 0, right: 0 };
        }
        const start = editor.view.coordsAtPos(from);
        const end = editor.view.coordsAtPos(to);
        const left = Math.min(start.left, end.left);
        const right = Math.max(start.right, end.right);
        return {
          x: left,
          y: start.top,
          width: right - left,
          height: end.bottom - start.top,
          top: start.top,
          bottom: end.bottom,
          left,
          right,
        };
      },
      // Lets autoUpdate observe the editor's scroll ancestors.
      contextElement: editor.view.dom,
    });
  }, [from, to, editor, floating.refs]);

  const kind = prompt?.kind;
  const actions = useMemo<Array<{ action: LinkPromptAction; label: string; pick: () => void }>>(
    () => [
      {
        action: 'previewOnly',
        label: 'community.composer.embed_prompt_preview',
        pick: () => {
          const chain = editor.chain().focus();
          (kind === 'embed' ? chain.convertLinkPromptToEmbed() : chain.showLinkPromptPreview()).run();
        },
      },
      {
        action: 'previewAndUrl',
        label: 'community.composer.embed_prompt_both',
        pick: () => {
          const chain = editor.chain().focus();
          (kind === 'embed' ? chain.appendLinkPromptEmbed() : chain.appendLinkPromptPreview()).run();
        },
      },
      {
        action: 'urlOnly',
        label: 'community.composer.embed_prompt_keep_link',
        pick: () => editor.chain().focus().dismissLinkPrompt().run(),
      },
    ],
    [editor, kind],
  );

  if (!prompt) return null;

  const activeIndex = actions.findIndex(({ action }) => action === prompt.active);
  const activeAction = actions[activeIndex];

  // Hover mirrors Tab/arrow navigation: entering a button makes it the active
  // action in plugin state, so the black pill follows the mouse too.
  const setActive = (active: LinkPromptAction) => {
    editor.commands.setLinkPromptActive(active);
  };

  return (
    <FloatingPortal>
      <div
        ref={floating.refs.setFloating}
        style={{ ...floating.floatingStyles, visibility: floating.isPositioned ? 'visible' : 'hidden' }}
        className={cn(POPOVER_PANEL_CLASSES, 'flex flex-col gap-1 p-1 z-[100]')}
        {...interactions.getFloatingProps()}
      >
        {/* Focus stays in the editor, so the label alone is the live region that
            announces the menu — buttons inside a status region are undefined
            behavior per spec. The active action rides along in it, since no
            screen reader ever reaches the buttons to read their state. */}
        <span role="status" className="px-3 pt-1 text-[11px] font-medium text-black/40">
          {tr(translations, 'community.composer.embed_prompt_paste_as')}
          <span className="sr-only"> {tr(translations, activeAction.label)}</span>
        </span>
        <div className="relative grid min-w-[200px] grid-cols-1 gap-1 sm:min-w-0 sm:grid-cols-3 sm:gap-0">
          {/* Equal-width columns let the pill slide with a pure-CSS translate — no measuring.
              Stacked rows below sm have no row to slide along, so they paint their own background. */}
          <span
            aria-hidden="true"
            className="absolute inset-y-0 left-0 hidden w-1/3 rounded-full bg-black transition-transform duration-200 ease-out sm:block"
            style={{ transform: `translateX(${activeIndex * 100}%)` }}
          />
          {actions.map(({ action, label, pick }) => (
            <button
              key={action}
              type="button"
              onClick={pick}
              onMouseEnter={() => setActive(action)}
              aria-current={prompt.active === action || undefined}
              className={cn(
                'relative z-10 inline-flex h-8 items-center justify-center rounded-full px-4 text-[13px] font-medium transition-colors duration-200',
                {
                  'bg-black text-white sm:bg-transparent': prompt.active === action,
                  'text-black/60': prompt.active !== action,
                },
              )}
            >
              {tr(translations, label)}
            </button>
          ))}
        </div>
      </div>
    </FloatingPortal>
  );
};
